import {Injectable} from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivate,
  NavigationStart,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import {Observable} from 'rxjs';
import {filter} from 'rxjs/operators';
import {UserSessionService} from "./user-session.service";

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {

  private cancelFlag: boolean= false;
  private isSubmitClicked: boolean = false;

  constructor(public router: Router,
              public userSessionService: UserSessionService,
              public route: ActivatedRoute) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (!this.userSessionService.data.maintenance_mode){
      this.checkMaintenance();
    }
    return this.determineAllowedRoutes(this.router.url, route.routeConfig.path);
  }
  checkMaintenance(){
    this.userSessionService.checkForSystemMaintenance();
  }
  //basically checks if browser back button is click
  initializeEventListeners(){
    this.router.events.pipe(filter((event)=>event instanceof NavigationStart))
    .subscribe((event: NavigationStart)=>{
      if(event.navigationTrigger === 'popstate'){
        this.isSubmitClicked = false;
      }
    });
  }

  private determineAllowedRoutes(current: string, destination: string){
    //check if they entered a policy number
    let potentialPolicyNumber = current.substring(1, current.length);
    // Fix: empty string should not be treated as a policy number
    if(potentialPolicyNumber && !isNaN(Number(potentialPolicyNumber))){
      return true;
    }

    if(this.userSessionService.data.maintenance_mode){
        if(destination == 'maintenance' ){
            return true;
        } else {
            this.router.navigate(['maintenance']);
        }
    }

    // User should always be able to return to instructions page
    if(destination == 'instructions' && this.cancelFlag){
      return true;
    }

    //you can only go from the first to the second
    if(current == '/' && destination == 'instructions'){
      return true;
    } else if (current == '/instructions' && destination == 'policy'){
      return true;
    } else if (current == '/policy' && destination == 'consent'){
      return true;
    } else if (current == '/consent' && destination == 'state-of-residence'){
      return true;
    } else if (current == '/state-of-residence' && destination == 'applicant-type'){
      return true;
    } else if (current == '/applicant-type' && destination == 'coverage'){
      return true;
    } else if (current == '/coverage' && destination == 'mhs'){
      return true;
    } else if (current.startsWith('/coverage') && destination == 'review'){
      return true;
    } else if (current == '/coverage' && destination == 'employee-information'){
      return true;
    } else if (current == '/employee-information' && destination == 'applicant-information'){
      return true;
    } else if (current.startsWith('/employee-information') && destination == 'review'){
      // Edit-from-review flow: user opened the employee section via
      // the Overview "View" button and is returning after Back / Save.
      return true;
    } else if (current.startsWith('/applicant-information') && destination == 'review'){
      // Edit-from-review flow: user opened the applicant section via
      // the Overview "View" button and is returning after Back / Save.
      return true;
    } else if (current.startsWith('/medical-history') && destination == 'review'){
      // Edit-from-review flow: user opened the medical history section via
      // the Overview "View" button and is returning after Back / Save.
      return true;
    } else if (current.startsWith('/physician-information') && destination == 'review'){
      // Edit-from-review flow: user opened the physician section via
      // the Overview "View" button and is returning after Back / Save.
      return true;
    } else if (current == '/applicant-information' && destination == 'medical-history'){
      return true;
    } else if (current == '/medical-history' && destination == 'physician-information'){
      return true;
    } else if (current == '/physician-information' && destination == 'review'){
      return true;
    } else if (current.startsWith('/review') && destination == 'coverage'){
      return true;
    } else if (current.startsWith('/auth-elec-sign') &&
              (destination == 'employee-information' ||
               destination == 'applicant-information' ||
               destination == 'medical-history' ||
               destination == 'physician-information')) {
      // Auth-elec-sign section-review flow: Overview View buttons
      // should always open their target section directly.
      return true;
    // } else if (current == '/authorization' && destination == 'electronic-communication'){
    //   return true;
    // } else if (current == '/electronic-communication' && destination == 'review'){
    //   return true;
    } else if (current == '/review' && destination == 'confirmation'){
      return true;
    } else if (current == '/authorization' && destination == 'confirmation'){
      return true;
    // Backward navigation rules for browser back button (MHS pages)
    } else if (current == '/applicant-information' && destination == 'employee-information'){
      return true;
    } else if (current == '/medical-history' && destination == 'applicant-information'){
      return true;
    } else if (current == '/physician-information' && destination == 'medical-history'){
      return true;
    } else if (current == '/authorization' && destination == 'physician-information'){
      return true;
    } else if (current == '/electronic-communication' && destination == 'authorization'){
      return true;
    } else if (current == '/mhs' && destination == 'confirmation'){
      return true;
    } else if (current == '/page-not-found' && destination == 'instructions'){
      return true;
    } else if (current == '/state-of-residence' && destination == 'esign-disclosure'){
      return true;
    } else if (current == '/esign-disclosure' && destination == 'applicant-type'){
      return true;
    } else if (current == '/' && destination == 'maintenance'){
      return true;
    } else if (current == '/maintenance' && destination == 'instructions'){
      return true;
    } else if (current == '/policy' && destination == 'instructions'){
      return true;
    }
    // Allow navigation between MHS sub-pages (for progress bar navigation)
    const mhsPages = ['employee-information', 'applicant-information', 'medical-history', 'physician-information', 'authorization', 'electronic-communication'];
    const currentIsMhsPage = mhsPages.some(page => current.includes(page));
    const destinationIsMhsPage = mhsPages.includes(destination);

    if (destinationIsMhsPage) {
      // Check if authorization page exists (6 steps) or not (5 steps)
      const isGenericDynamicForm = this.userSessionService.data.isGenericDynamicForm;
      // Get the step number for the destination
      let stepMap;
      if (isGenericDynamicForm) {
        // 6 steps: authorization page included
        stepMap = {
          'employee-information': 1,
          'applicant-information': 2,
          'medical-history': 3,
          'physician-information': 4,
          'authorization': 5,
          'electronic-communication': 6
        };
      } else {
        // 5 steps: authorization page skipped, electronic-communication is step 5
        stepMap = {
          'employee-information': 1,
          'applicant-information': 2,
          'medical-history': 3,
          'physician-information': 4,
          'electronic-communication': 5
        };

        // If trying to access authorization page when it doesn't exist, redirect
        if (destination === 'authorization') {
          this.router.navigate(['employee-information']);
          return false;
        }
      }

      const destinationStep = stepMap[destination];
      // Always allow access to step 1 (employee-information)
      if (destinationStep === 1) {
        return true;
      }

      // For other steps, check if previous step is completed
      const completedSteps = this.userSessionService.data['mhsCompletedSteps'];
      if (completedSteps && completedSteps[destinationStep - 2] === true) {
        // Previous step is completed, allow navigation
        return true;
      } else {
        // Previous step not completed or no session data
        // Check if session is valid (has policy which is set before MHS)
        const hasPolicy = this.userSessionService.data.policy;

        if (!hasPolicy) {
          // No session data, redirect to instructions (start of flow)
          this.router.navigate(['instructions']);
        } else {
          // Session exists but previous steps not completed, redirect to first MHS page
          this.router.navigate(['employee-information']);
        }
        return false;
      }
    }

    //TODO Timeout
    return false;
  }

  public cancelLeaveAnyway(){
    this.cancelFlag = true;
  }

}
